<template>
    <div v-if="authStore.isAuthenticated">
        <div v-if="canAccess">
            <div v-if="loading">Chargement...</div>

            <div v-else-if="club" class="module">
                <h2 class="page-title">Paramètres de {{ club.name }}</h2>

                <!-- Statut premium -->
                <div class="premium-banner" :class="premium?.enabled ? 'is-active' : 'is-inactive'">
                    <p v-if="premium?.enabled">
                        ✅ Gestion d'événements premium active
                        <span class="premium-source">({{ premiumSourceLabel }})</span><template v-if="premium.until">,
                            jusqu'au {{ formatDate(premium.until) }}</template>.
                        Vous pouvez configurer HelloAsso ci-dessous et gérer vos inscriptions.
                    </p>
                    <template v-else>
                        <p>
                            🔒 La gestion d'événements (inscriptions, check-in, paiement HelloAsso) est un service
                            premium, pas encore activé pour {{ club.name }}.
                        </p>
                        <div class="premium-actions">
                            <router-link to="/services#tarifs" class="btn-secondary">Voir les tarifs</router-link>
                            <a :href="`${authUrl}/contact`" target="_blank" rel="noopener noreferrer" class="btn-submit">
                                Nous contacter
                            </a>
                        </div>
                    </template>
                </div>

                <template v-if="premium?.enabled">
                    <!-- Configuration HelloAsso -->
                    <div class="settings-section">
                        <h3>Configuration HelloAsso</h3>
                        <p class="hint">
                            Ces identifiants permettent à DartsHub d'encaisser les paiements de vos événements
                            directement sur votre compte HelloAsso.
                        </p>

                        <div class="form-group">
                            <label>Identifiant d'organisation (slug)</label>
                            <input v-model="form.helloasso_org_slug" class="form-input"
                                placeholder="ex : mon-club-de-darts" />
                        </div>

                        <div class="form-group">
                            <label>Client ID</label>
                            <input v-model="form.helloasso_client_id" class="form-input" autocomplete="off"
                                placeholder="Client ID API HelloAsso" />
                        </div>

                        <div class="form-group">
                            <label>Client Secret</label>
                            <input v-model="form.helloasso_client_secret" type="text" class="form-input masked-input"
                                autocomplete="off" spellcheck="false" data-1p-ignore data-lpignore="true"
                                data-bwignore data-form-type="other"
                                :placeholder="haStatus?.configured ? 'Laisser vide pour ne pas changer' : 'Client Secret API HelloAsso'" />
                        </div>

                        <div class="form-actions">
                            <button class="btn-submit" :disabled="saving" @click="saveHelloAsso">
                                {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
                            </button>
                            <button class="btn-secondary" :disabled="testing || !haStatus?.configured"
                                @click="testConnection">
                                {{ testing ? 'Test en cours...' : 'Tester la connexion' }}
                            </button>
                        </div>

                        <div v-if="haStatus?.webhook_url" class="webhook-block">
                            <label>URL de webhook à coller dans HelloAsso</label>
                            <div class="webhook-row">
                                <input class="form-input webhook-input" readonly :value="haStatus.webhook_url"
                                    @focus="($event.target as HTMLInputElement).select()" />
                                <button class="btn-secondary" @click="copyWebhook">
                                    {{ copied ? 'Copié !' : 'Copier' }}
                                </button>
                            </div>
                            <p class="hint">
                                Collez cette URL dans votre espace HelloAsso, section notifications/webhooks, pour que
                                DartsHub soit informé de chaque paiement.
                            </p>
                        </div>
                    </div>

                    <!-- Guide -->
                    <div class="settings-section">
                        <h3>Guide de connexion HelloAsso</h3>
                        <ol class="guide-list">
                            <li>
                                Connectez-vous à votre espace organisation sur
                                <a href="https://admin.helloasso.com" target="_blank" rel="noopener">admin.helloasso.com</a>.
                            </li>
                            <li>
                                L'identifiant d'organisation (slug) est le nom qui apparaît dans l'URL de votre page
                                HelloAsso, par exemple <code>helloasso.com/associations/mon-club-de-darts</code>,
                                le slug est alors <code>mon-club-de-darts</code>.
                            </li>
                            <li>
                                Dans votre espace HelloAsso, section Développeurs / API, créez une application pour
                                obtenir un Client ID et un Client Secret, puis collez-les dans le formulaire ci-dessus.
                            </li>
                            <li>
                                Enregistrez, puis copiez l'URL de webhook affichée et collez-la dans la section
                                notifications de votre espace HelloAsso.
                            </li>
                            <li>Cliquez sur "Tester la connexion" pour vérifier que tout fonctionne.</li>
                        </ol>
                        <p class="hint">
                            Les noms exacts des menus HelloAsso peuvent évoluer avec leurs mises à jour, contactez-nous
                            si un intitulé ne correspond plus.
                        </p>
                    </div>
                </template>
            </div>
        </div>

        <div v-else class="centered-block">
            <p>Cette section est réservée aux représentants de club.</p>
            <router-link to="/" class="cta-link">← Retour à l'accueil</router-link>
        </div>
    </div>

    <div v-else class="centered-block">
        <h2>Connexion requise</h2>
        <p>Veuillez vous connecter pour accéder à cette section.</p>
        <button class="btn-primary" @click="authStore.openAuthModal">Connexion</button>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { useAuthStore } from '@/stores/useAuthStore'
import { formatDate, handleError } from '@/functions/utils'
import { auth_url as authUrl } from '@/config/config'

const authStore = useAuthStore()
const route = useRoute()

const isClubRep = computed(() => authStore.scopes.includes('club_representative'))
const isSuperAdmin = computed(() => authStore.scopes.includes('super_admin'))
// `?club=ID` : club ciblé explicitement (lien depuis la gestion d'inscriptions d'un
// événement, cf. EventInscriptions.vue::ownerSettings). Seul le super_admin, qui n'est
// représentant d'aucun club, en a besoin : le backend ignore le paramètre pour les
// autres (cf. clubs/routes.py::get_my_club), un représentant retombe sur son club.
const targetClubId = computed(() => {
    const n = Number(route.query.club)
    return Number.isInteger(n) && n > 0 ? n : null
})
const canAccess = computed(() => isClubRep.value || (isSuperAdmin.value && targetClubId.value !== null))

interface ClubInfo {
    id: number
    name: string
    number: number
    comite?: string | null
    ligue?: string | null
}

interface HelloAssoStatus {
    configured: boolean
    helloasso_org_slug: string | null
    helloasso_client_id: string | null
    webhook_url: string | null
}

interface PremiumStatus {
    enabled: boolean
    source: 'club' | 'comite' | 'ligue' | 'event' | null
    // Fin de validité (incluse), null = sans limite. Cf. clubs/schemas.py::PremiumStatus.
    until: string | null
}

const loading = ref(false)
const club = ref<ClubInfo | null>(null)
const haStatus = ref<HelloAssoStatus | null>(null)
const premium = ref<PremiumStatus | null>(null)
const saving = ref(false)
const testing = ref(false)
const copied = ref(false)

const form = ref({
    helloasso_org_slug: '',
    helloasso_client_id: '',
    helloasso_client_secret: '',
})

const premiumSourceLabel = computed(() => {
    if (premium.value?.source === 'comite') return 'via votre comité'
    if (premium.value?.source === 'ligue') return 'via votre ligue'
    if (premium.value?.source === 'event') return 'sur un événement précis'
    return 'sur votre club'
})

const applyHaStatusToForm = (status: HelloAssoStatus) => {
    form.value.helloasso_org_slug = status.helloasso_org_slug ?? ''
    form.value.helloasso_client_id = status.helloasso_client_id ?? ''
    form.value.helloasso_client_secret = ''
}

const fetchAll = async () => {
    loading.value = true
    try {
        const { data: clubData } = await dartsHubApi.get('/clubs/my-club', {
            params: targetClubId.value !== null ? { club_id: targetClubId.value } : {},
        })
        club.value = clubData

        const [haRes, premiumRes] = await Promise.all([
            dartsHubApi.get(`/clubs/${clubData.id}/helloasso`),
            dartsHubApi.get(`/clubs/${clubData.id}/premium-events-status`),
        ])
        haStatus.value = haRes.data
        applyHaStatusToForm(haRes.data)
        premium.value = premiumRes.data
    } catch (err) {
        handleError(err, 'loading club settings')
    } finally {
        loading.value = false
    }
}

const saveHelloAsso = async () => {
    if (!club.value) return
    saving.value = true
    try {
        const payload: Record<string, string> = {
            helloasso_org_slug: form.value.helloasso_org_slug,
            helloasso_client_id: form.value.helloasso_client_id,
        }
        if (form.value.helloasso_client_secret) {
            payload.helloasso_client_secret = form.value.helloasso_client_secret
        }
        const { data } = await dartsHubApi.patch(`/clubs/${club.value.id}/helloasso`, payload)
        haStatus.value = data
        applyHaStatusToForm(data)
        toast.success('Paramètres HelloAsso enregistrés.')
    } catch (err) {
        handleError(err, 'saving HelloAsso settings')
    } finally {
        saving.value = false
    }
}

const testConnection = async () => {
    if (!club.value) return
    testing.value = true
    try {
        await dartsHubApi.post(`/clubs/${club.value.id}/helloasso/test`)
        toast.success('Connexion HelloAsso réussie.')
    } catch (err) {
        handleError(err, 'testing HelloAsso connection')
    } finally {
        testing.value = false
    }
}

const copyWebhook = async () => {
    if (!haStatus.value?.webhook_url) return
    try {
        await navigator.clipboard.writeText(haStatus.value.webhook_url)
        copied.value = true
        setTimeout(() => { copied.value = false }, 1500)
    } catch {
        // Presse-papier indisponible : l'utilisateur peut toujours sélectionner l'URL à la main.
    }
}

onMounted(() => {
    if (canAccess.value) fetchAll()
})
</script>

<style scoped>
/* Secret API masqué sans type="password" : sinon le navigateur le prend pour un mot de
   passe du site (propose d'en générer un, puis de l'enregistrer). */
.masked-input {
    -webkit-text-security: disc;
}
.premium-banner {
    padding: 0.9rem 1rem;
    border-radius: var(--radius, 8px);
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
}

.premium-actions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-top: 0.75rem;
}

.premium-banner.is-active {
    background: rgba(56, 199, 147, 0.12);
    color: var(--color-success, #38c793);
    border: 1px solid var(--color-success, #38c793);
}

.premium-banner.is-inactive {
    background: rgba(245, 165, 36, 0.1);
    color: var(--color-accent, #f5a524);
    border: 1px solid var(--color-accent, #f5a524);
}

.premium-source {
    font-style: italic;
}

.settings-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--color-light-shadow, #ddd);
}

.settings-section h3 {
    margin-bottom: 0.5rem;
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
    margin: 0.25rem 0 0.75rem;
}

.form-group {
    margin-bottom: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.form-group label {
    font-weight: 600;
    font-size: 0.9rem;
}

.form-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 1rem;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: var(--radius, 8px);
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    border: 1px solid var(--color-accent, #1a3a5c);
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled,
.btn-secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}

.webhook-block {
    margin-top: 1.25rem;
}

.webhook-row {
    display: flex;
    gap: 0.5rem;
}

.webhook-input {
    font-family: monospace;
    font-size: 0.85rem;
}

.guide-list {
    padding-left: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.guide-list code {
    background: var(--color-bg-soft, rgba(0, 0, 0, 0.05));
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    font-size: 0.85rem;
}

.guide-list a {
    color: var(--color-accent, #f5a524);
    text-decoration: none;
    font-weight: 600;
}

.guide-list a:hover {
    text-decoration: underline;
}

.centered-block {
    text-align: center;
    padding: 3rem 1rem;
}
</style>
